import React from 'react';
import { motion } from 'framer-motion';
import { Slide, FadeIn, SlideHeading, StaggerContainer, StaggerItem, Card } from './Shared';
import { ChevronDown, Target, Layers, Globe, Clock, User, Camera, Printer, Zap, Flame, Scissors, Warehouse, BoxIcon, Lightbulb } from 'lucide-react';
export const Slide1Cover = () => <Slide id="slide-1" className="items-center text-center" bg="white" data-id="element-210">
    <FadeIn delay={0.2} data-id="element-211">
      <img src="https://cdn.magicpatterns.com/uploads/aYvYevoG5MfgByUVejQyfu/LOGO_STRATA_S.png" alt="STRATA" className="w-48 md:w-60 lg:w-72 h-auto mx-auto mb-8" data-id="element-212" />
    </FadeIn>

    <FadeIn delay={0.4} data-id="element-213">
      <h1 className="text-6xl md:text-8xl lg:text-9xl font-serif font-bold text-black mb-6 tracking-tight uppercase leading-none" data-id="element-214">
        STRATA <br data-id="element-215" />
        <span className="text-4xl md:text-6xl lg:text-7xl text-slate-500" data-id="element-216">
          Creative Group
        </span>
      </h1>
    </FadeIn>

    <FadeIn delay={0.6} data-id="element-217">
      <p className="text-2xl md:text-4xl text-black font-serif uppercase max-w-4xl mx-auto leading-tight mb-8" data-id="element-218">
        Real Infrastructure. Real Experience. Ready to Scale.
      </p>
    </FadeIn>

    <FadeIn delay={0.8} data-id="element-219">
      <div className="flex flex-wrap justify-center gap-4 text-sm md:text-base text-slate-600 font-bold tracking-widest uppercase" data-id="element-220">
        <span data-id="element-221">Production</span>
        <span className="text-black" data-id="element-222">•</span>
        <span data-id="element-223">Ecommerce</span>
        <span className="text-black" data-id="element-224">•</span>
        <span data-id="element-225">Brands</span>
        <span className="text-black" data-id="element-226">•</span>
        <span data-id="element-227">B2B Apparel</span>
      </div>
    </FadeIn>

    <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    delay: 1.5,
    duration: 1
  }} className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce" data-id="element-228">
      <ChevronDown className="w-8 h-8 text-black" data-id="element-229" />
    </motion.div>
  </Slide>;
export const Slide2ExecutiveSummary = () => <Slide id="slide-2" bg="gray" data-id="element-230">
    <SlideHeading tag="Executive Summary" title="We Are Operational" subtitle="This is not a pitch deck built on ideas. It's built on iron." data-id="element-231" />

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-232">
      <FadeIn delay={0.2} data-id="element-233">
        <p className="text-xl md:text-2xl text-slate-700 leading-relaxed mb-8 font-medium" data-id="element-234">
          We've invested heavily in building a fully operational apparel
          production facility. Equipment is installed, systems are live, and
          we've completed our first B2B orders.
        </p>
        <p className="text-lg md:text-xl text-slate-600 leading-relaxed" data-id="element-235">
          Now we're raising a strategic round from our network to fuel customer
          acquisition and scale operations without putting unnecessary pressure
          on the partnership during the startup phase.
        </p>
      </FadeIn>

      <StaggerContainer className="grid grid-cols-2 gap-4 md:gap-6" data-id="element-236">
        {[{
        value: '1B+',
        label: 'Garments Produced',
        icon: Target
      }, {
        value: '$600K+',
        label: 'Ready-to-Print IP',
        icon: Layers
      }, {
        value: 'LIVE',
        label: 'Doors Are Open',
        icon: Clock
      }, {
        value: '3',
        label: 'Country Team',
        icon: Globe
      }].map((kpi, i) => <StaggerItem key={i} data-id="element-237">
            <Card dark className="text-center py-10 flex flex-col items-center justify-center h-full" data-id="element-238">
              <kpi.icon className="w-8 h-8 text-white mx-auto mb-4" data-id="element-239" />
              <div className="text-5xl md:text-6xl font-serif font-bold text-white mb-2" data-id="element-240">
                {kpi.value}
              </div>
              <div className="text-sm md:text-base text-slate-300 font-bold uppercase tracking-widest" data-id="element-241">
                {kpi.label}
              </div>
            </Card>
          </StaggerItem>)}
      </StaggerContainer>
    </div>
  </Slide>;
export const Slide3Founders = () => <Slide id="slide-3" bg="white" data-id="element-242">
    <SlideHeading tag="The Team" title="Meet the Founders" subtitle="Decades of world-class production experience now working for you." data-id="element-243" />

    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-8" data-id="element-244">
      <FadeIn delay={0.2} data-id="element-245">
        <Card className="h-full flex flex-col" data-id="element-246">
          <div className="w-full h-[28rem] border-2 border-black mb-6 overflow-hidden bg-black" data-id="element-247">
            <img src="https://cdn.magicpatterns.com/uploads/iH8vEpR1PNqZ9Foi3svT3y/FRANCISCO_KELLY.jpg" alt="Francisco Kelly" className="w-full h-full object-cover object-[center_30%]" data-id="element-248" />
          </div>
          <h3 className="text-3xl font-serif font-bold text-black uppercase mb-2" data-id="element-249">
            Francisco Kelly
          </h3>
          <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-4" data-id="element-250">
            CEO — Operations, Infrastructure & Expansion
          </p>
          <p className="text-slate-600 text-lg leading-relaxed" data-id="element-251">
            A multidisciplinary operator with experience across textile,
            entertainment, and restaurant businesses, and owner of a
            construction company specializing in commercial build-outs. Brings
            operational structure, logistical expertise, and expansion
            capabilities to STRATA's physical and commercial footprint.
          </p>
        </Card>
      </FadeIn>

      <FadeIn delay={0.4} data-id="element-252">
        <Card className="h-full flex flex-col" data-id="element-253">
          <div className="w-full h-[28rem] border-2 border-black mb-6 overflow-hidden bg-black" data-id="element-254">
            <img src="https://cdn.magicpatterns.com/uploads/83mvhjpptRvavtj5uBs1iM/SANTIAGO_COHEN.png" alt="Santiago Cohen" className="w-full h-full object-cover object-top" data-id="element-255" />
          </div>
          <h3 className="text-3xl font-serif font-bold text-black uppercase mb-2" data-id="element-256">
            Santiago Cohen
          </h3>
          <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-4" data-id="element-257">
            COO — Head of Creative Manufacturing
          </p>
          <p className="text-slate-600 text-lg leading-relaxed" data-id="element-258">
            <strong className="text-black font-bold" data-id="element-259">30+ years</strong> in
            screen printing, color separation, apparel design, and high-volume
            production across the U.S. and Mexico. Has overseen production of{' '}
            <strong className="text-black font-bold" data-id="element-260">
              1 billion+ printed garments
            </strong>{' '}
            and created{' '}
            <strong className="text-black font-bold" data-id="element-261">
              3,000+ original designs
            </strong>{' '}
            for brands including Walmart, Target, Warner Bros., Nike, Gap, Old
            Navy, Marvel, and Disney Consumer Products. A specialist in workflow
            engineering, automation, and scalable production systems.
          </p>
        </Card>
      </FadeIn>
    </div>
  </Slide>;
export const Slide4WhatsBuilt = () => <Slide id="slide-4" bg="gray" data-id="element-262">
    <SlideHeading tag="In-House Equipment" title="Our Production Arsenal" subtitle="No middlemen. No outsourcing your order. We own every machine and run every job under our own roof." data-id="element-263" />

    <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-id="element-264">
      {[{
      title: 'STS DTF Printer',
      desc: 'Top of the line direct-to-film',
      icon: Printer
    }, {
      title: 'STS Powder Shaker',
      desc: 'Conveyor dryer and cure unit',
      icon: Flame
    }, {
      title: 'Epson DTG Printer',
      desc: 'Direct-to-garment production',
      icon: Printer
    }, {
      title: 'Pre-Treatment Station',
      desc: 'Garment prep system',
      icon: BoxIcon
    }, {
      title: 'Laser Cutters/Engravers',
      desc: 'Leather patches, wood, acrylic',
      icon: Scissors
    }, {
      title: 'Epson Film Positives Printer',
      desc: 'Large format specialty printer',
      icon: Printer
    }, {
      title: 'Screen Exposure Unit',
      desc: 'Precision screen burning for print production',
      icon: Lightbulb
    }, {
      title: 'Hopkins Manual Press',
      desc: 'Top tier 8 color 6 station screen printing',
      icon: Zap
    }, {
      title: '2-Head Embroidery',
      desc: 'Premium custom stitching',
      icon: Layers
    }, {
      title: 'Conveyor Oven/Dryer',
      desc: 'Industrial curing',
      icon: Flame
    }, {
      title: 'Heat Presses',
      desc: 'Multiple transfer stations',
      icon: BoxIcon
    }, {
      title: 'Full Warehouse',
      desc: 'Rented and operational',
      icon: Warehouse
    }].map((item, i) => <StaggerItem key={i} data-id="element-265">
          <Card className="flex items-start gap-4 p-6 h-full" data-id="element-266">
            <div className="w-12 h-12 bg-black flex items-center justify-center shrink-0" data-id="element-267">
              <item.icon className="w-6 h-6 text-white" data-id="element-268" />
            </div>
            <div data-id="element-269">
              <h4 className="text-xl font-serif font-bold text-black uppercase mb-1" data-id="element-270">
                {item.title}
              </h4>
              <p className="text-slate-600 text-sm font-medium" data-id="element-271">{item.desc}</p>
            </div>
          </Card>
        </StaggerItem>)}
    </StaggerContainer>
  </Slide>;
export const Slide5DesignArsenal = () => <Slide id="slide-5" bg="black" data-id="element-272">
    <SlideHeading dark tag="Our Own Brand" title="Ke Chingón" subtitle="We built our own apparel brand. We pour the same craft into it that we'll pour into yours." data-id="element-273" />

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-8 items-center" data-id="element-274">
      <FadeIn delay={0.2} className="lg:col-span-5" data-id="element-275">
        <p className="text-xl md:text-2xl text-slate-200 leading-relaxed mb-8 font-medium" data-id="element-276">
          Ke Chingón isn't a side project — it's proof. Proof that we don't just
          print what you hand us, we understand how to build a brand that turns
          heads.
        </p>
        <p className="text-lg text-slate-400 leading-relaxed mb-10" data-id="element-277">
          Every piece is designed, separated, and produced in-house. When you
          work with us, you tap into that same creative firepower for your own
          brand or event.
        </p>
        <div className="border-l-4 border-white pl-6" data-id="element-278">
          <p className="text-2xl md:text-3xl font-serif font-bold text-white italic leading-tight" data-id="element-279">
            "We have our own brand — so we know exactly how to make yours look
            chingón."
          </p>
        </div>
      </FadeIn>

      <StaggerContainer className="lg:col-span-7 grid grid-cols-2 gap-4 md:gap-6" data-id="element-280">
        {[{
        url: 'https://cdn.magicpatterns.com/uploads/iCW3Ff7ug61246qm4xrV4c/pasted-image.png',
        alt: 'Golden ornate jaguar design'
      }, {
        url: 'https://cdn.magicpatterns.com/uploads/rufBaBetS8ui92oCXRGtVe/pasted-image.png',
        alt: 'Aztec warrior with jaguar headdress design'
      }, {
        url: 'https://cdn.magicpatterns.com/uploads/uyJoCrFhXokjNw4cr8iEg3/pasted-image.png',
        alt: 'Decorated sugar skull design'
      }, {
        url: 'https://cdn.magicpatterns.com/uploads/8aZAiiTyue4h43k68hWWRH/pasted-image.png',
        alt: 'Black panther with golden eyes design'
      }].map((img, i) => <StaggerItem key={i} data-id="element-281">
            <div className="group relative aspect-[4/5] overflow-hidden border-4 border-white bg-black" data-id="element-282">
              <img src={img.url} alt={img.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-283" />
            </div>
          </StaggerItem>)}
      </StaggerContainer>
    </div>
  </Slide>;